import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  Typography,
  Divider,
} from '@mui/material'
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import type { ClassGroup } from '@timetable/types'

interface ClassGroupsDialogProps {
  open: boolean
  schoolId: string
  groups: ClassGroup[]
  onClose: () => void
  onCreate: (name: string) => Promise<unknown>
  onUpdate: (id: string, name: string) => Promise<unknown>
  onDelete: (id: string) => Promise<unknown>
}

export const ClassGroupsDialog: React.FC<ClassGroupsDialogProps> = ({
  open,
  groups,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
}) => {
  const [newGroupName, setNewGroupName] = React.useState('')
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [editingName, setEditingName] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    if (!open) {
      setNewGroupName('')
      setEditingId(null)
      setEditingName('')
      setError(null)
    }
  }, [open])

  const handleCreate = async () => {
    const trimmed = newGroupName.trim()
    if (!trimmed) return
    if (groups.some((g) => g.name.toLowerCase() === trimmed.toLowerCase())) {
      setError(`"${trimmed}" already exists`)
      return
    }
    setError(null)
    setLoading(true)
    try {
      await onCreate(trimmed)
      setNewGroupName('')
    } catch (e: any) {
      setError(e.message || 'Failed to create group')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdate = async (id: string) => {
    const trimmed = editingName.trim()
    if (!trimmed) return
    if (groups.some((g) => g.name.toLowerCase() === trimmed.toLowerCase() && g.id !== id)) {
      setError(`"${trimmed}" already exists`)
      return
    }
    setError(null)
    setLoading(true)
    try {
      await onUpdate(id, trimmed)
      setEditingId(null)
    } catch (e: any) {
      setError(e.message || 'Failed to update group')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    setError(null)
    setLoading(true)
    try {
      await onDelete(id)
    } catch (e: any) {
      setError(e.message || 'Failed to delete group. It may be in use by existing classes.')
    } finally {
      setLoading(false)
    }
  }

  const startEdit = (group: ClassGroup) => {
    setEditingId(group.id)
    setEditingName(group.name)
    setError(null)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Manage Class Groups</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          {/* Add new group */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="New group name"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              size="small"
              fullWidth
              placeholder="e.g., High School"
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              disabled={loading}
            />
            <IconButton
              color="primary"
              onClick={handleCreate}
              disabled={!newGroupName.trim() || loading}
              title="Add group"
            >
              <AddIcon />
            </IconButton>
          </Box>

          <Divider />

          {groups.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
              No class groups yet. Add one above.
            </Typography>
          ) : (
            <List dense disablePadding>
              {groups.map((group) => (
                <ListItem key={group.id} disablePadding sx={{ py: 0.5 }}>
                  {editingId === group.id ? (
                    <Box sx={{ display: 'flex', gap: 1, width: '100%', alignItems: 'center' }}>
                      <TextField
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        size="small"
                        fullWidth
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleUpdate(group.id)
                          if (e.key === 'Escape') setEditingId(null)
                        }}
                        disabled={loading}
                      />
                      <IconButton size="small" color="primary" onClick={() => handleUpdate(group.id)} disabled={loading}>
                        <CheckIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => setEditingId(null)} disabled={loading}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      <ListItemText primary={group.name} />
                      <ListItemSecondaryAction>
                        <IconButton size="small" onClick={() => startEdit(group)} disabled={loading}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(group.id)} disabled={loading}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  )
}
